import { useSelectedTableRows } from "@/components/data-table/hooks/useSelectedTableRows";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { ContractorsPlannedHoursWizardService } from "@/services/contractorsPlannedHoursWizardService";
import { ApiError } from "@/services/api";
import {
  CalculateContractorsPlannedHoursWizardRequest,
  DisplayContractorsPlannedHoursWizardAllocation,
  ReadContractorsPlannedHoursWizardResult,
} from "@/types/contractorsPlannedHoursWizard";
import { useCallback, useMemo, useState } from "react";

export type ContractorsPlannedHoursWizardTabId = "selecting" | "results";

export function useContractorsPlannedHoursWizardCalculate() {
  const [calculationResults, setCalculationResults] = useState<
    ReadContractorsPlannedHoursWizardResult[]
  >([]);
  const [activeTab, setActiveTab] =
    useState<ContractorsPlannedHoursWizardTabId>("selecting");

  const calculateMutation = useMutationWithErrorHandling<
    ReadContractorsPlannedHoursWizardResult[],
    ApiError,
    CalculateContractorsPlannedHoursWizardRequest,
    unknown
  >({
    mutationFn: ContractorsPlannedHoursWizardService.calculate,
    onSuccess: (data) => {
      setCalculationResults(data);
      setActiveTab("results");
    },
  });

  const selectedRows =
    useSelectedTableRows<DisplayContractorsPlannedHoursWizardAllocation>();

  const selectedIds = useMemo(
    () => selectedRows.map((row) => row.id),
    [selectedRows],
  );

  const handleCalculate = useCallback(() => {
    const uniqueIds = [...new Set(selectedIds)];
    if (uniqueIds.length === 0) {
      setCalculationResults([]);
      return;
    }
    calculateMutation.mutate({ schedules: uniqueIds });
  }, [selectedIds, calculateMutation]);

  const calculateDisabled =
    selectedIds.length === 0 || calculateMutation.isPending;

  return {
    activeTab,
    setActiveTab,
    calculationResults,
    calculateMutation,
    handleCalculate,
    calculateDisabled,
  };
}
